import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ExternalActivityDto, FinishRunResponse } from '@rw/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, QueryView } from '../../components/states';
import { Button } from '../../components/ui';
import { invalidateCompetitive, qk } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDistance, formatDuration, formatRunWhen } from '../../lib/format';
import { toast } from '../../stores/toasts';
import { t as tr } from '../../i18n';

export function StravaImportPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['strava', 'activities'], queryFn: () => api<ExternalActivityDto[]>('/integrations/strava/activities') });
  const [busy, setBusy] = useState<string | null>(null);
  const imp = useMutation({
    mutationFn: (id: string) => api<FinishRunResponse>(`/integrations/strava/activities/${id}/import`, { method: 'POST', timeoutMs: 60_000 }),
    onMutate: (id) => setBusy(id),
    onSettled: () => setBusy(null),
    onSuccess: (r) => {
      qc.setQueryData(qk.run(r.run.id), r.run);
      void qc.invalidateQueries({ queryKey: qk.runs });
      void qc.invalidateQueries({ queryKey: ['strava'] });
      invalidateCompetitive(qc);
      navigate(`/runs/${r.run.id}`, { state: { fresh: true } });
    },
    onError: (e) => toast({ kind: 'error', title: tr('Import failed'), body: e instanceof ApiError ? e.message : undefined }, 7000),
  });

  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {tr('← Back')}
      </button>
      <h1 className="font-display text-3xl font-bold uppercase">{tr('Import runs')}</h1>
      <p className="mt-1 text-sm text-ink-400">{tr('Runs from the last 7 days. Imported runs are validated like live runs and can capture territory.')}</p>
      <div className="mt-4">
        <QueryView query={q} empty={(d) => (d.length === 0 ? <EmptyState icon="🏃" title={tr('No recent runs on Strava')} /> : null)}>
          {(list) => (
            <ul className="space-y-2" data-testid="strava-activities">
              {list.map((a) => {
                const d = formatDistance(a.distanceM);
                return (
                  <li key={a.id} className="flex items-center gap-3 rounded-2xl border border-ink-700/80 bg-ink-850 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{a.name}</div>
                      <div className="text-xs text-ink-400">
                        {formatRunWhen(a.startedAt)} · {d.value} {d.unit} · {formatDuration(a.movingTimeS)}
                      </div>
                      {a.blockedReason ? <div className="text-xs text-warn">{a.blockedReason}</div> : null}
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#FC5200]">
                        {tr('View on Strava')}
                      </a>
                    </div>
                    {a.importedRunId ? (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/runs/${a.importedRunId}`)}>
                        {tr('View')}
                      </Button>
                    ) : (
                      <Button size="sm" disabled={!!a.blockedReason || !!busy} loading={busy === a.id} onClick={() => imp.mutate(a.id)}>
                        {tr('Import')}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </QueryView>
      </div>
      <img src="/brand/strava/api_logo_pwrdBy_strava_horiz_white.svg" alt="Powered by Strava" className="mx-auto mt-8 h-4" />
    </Page>
  );
}
